import { cloneDeep, has, isArray, omit, unset } from 'lodash';
import { DUPLICATE_OF, NEW_STATE } from './ServiceTestsConstants';

/**
 * Fields to remove from a test when duplicating.
 * Matches portal/ui tests.service.js:duplicateTest() (lines 280-302).
 */
const PERSISTENCE_FIELDS = ['_id', 'legacyId', 'testsLegacyId', 'locationKeyId'];

/**
 * Duplicate a test object for copy operations.
 * Mirrors portal/ui tests.service.js:duplicateTest() exactly:
 *   1. Deep clone and strip persistence IDs
 *   2. Append " (Copy)" to name
 *   3. Clear beaconSteps[*].locationKeyId
 *   4. Clear testConfig.auth._id (web tests)
 *   5. Clear testConfig.httpHeaders[*]._id (web tests)
 *   6. Set copiedFrom for audit tracking
 *
 * @param {Object} sourceTest - The source test object from API
 * @param {string} dupOfId - The source test's _id (for copiedFrom tracking)
 * @returns {Object} Cleaned duplicate ready for form defaultValues
 */
export function duplicateTestData(sourceTest, dupOfId) {
    const dup = omit(cloneDeep(sourceTest), PERSISTENCE_FIELDS);

    dup.name = `${dup.name} (Copy)`;

    if (isArray(dup.beaconSteps)) {
        dup.beaconSteps.forEach((step) => delete step.locationKeyId);
    }

    if (has(dup, 'testConfig.auth')) {
        unset(dup, 'testConfig.auth._id');
    }

    if (has(dup, 'testConfig.httpHeaders') && isArray(dup.testConfig.httpHeaders)) {
        dup.testConfig.httpHeaders.forEach((header) => delete header._id);
    }

    dup.copiedFrom = dupOfId;

    return dup;
}

/**
 * Build the navigation path for copying a test.
 * Used by List Copy button, Detail Copy button, and Save & Copy action.
 *
 * @param {string} testId - The source test ID to copy from
 * @param {Object} [options] - Navigation options
 * @param {boolean} [options.fromDetail=false] - When true, prepends "../" so
 *   React Router resolves relative to the parent /tests/ route instead of
 *   the current /tests/:_id segment.
 * @returns {string} Navigation path, e.g. "new?dupOf=abc123"
 */
export function buildCopyNavigationPath(testId, { fromDetail = false } = {}) {
    const path = `${NEW_STATE}?${DUPLICATE_OF}=${testId}`;
    return fromDetail ? `../${path}` : path;
}
